import React, { type ReactElement, useEffect, useRef, useState } from 'react';
import { apiGet, type Resource } from '../../js/api';
import {
    sanitizeCollectionResponse,
    type SanitizedCollection
} from '../../js/helpers';
import { type CollectionResponse } from '../../api/types/response';
import Pagination from '../pagination/Pagination';
import Loader from '../loader/Loader';
import ErrorMessage from '../errorMessage/ErrorMessage';
import { type SearchParams } from '../../api/search';
import _server from '../../js/config/_server';
import { omit, equals } from 'ramda';
import queryString from 'query-string';
import { useNavigate } from 'react-router';

type ApiEndpoint = string;

type PaginatorProps<T> = {
    children: (
        paginatedData: T[],
        totalItems: number,
        isLoading: boolean
    ) => ReactElement;
    /** If false, URL wont be updated with search params */
    isUpdateLocationEnabled?: boolean;
    params?: SearchParams;
    preFetch?: boolean;
    resetCache?: boolean | number;
    scrollIntoViewElement?: HTMLElement | undefined | null;
    source: ApiEndpoint;
    updateList?: boolean;
};

const initialCache = {
    0: {
        list: [],
        totalItems: -1,
        currentPage: -1,
        lastPage: -1,
        totalPages: -1,
    }
};

const Paginator = <TResource extends Resource>(
    props: PaginatorProps<TResource>
) => {
    const {
        children,
        isUpdateLocationEnabled = true,
        params,
        preFetch,
        scrollIntoViewElement,
        source,
        updateList,
    } = props;

    const navigate = useNavigate();

    const [paginatedData, setPaginatedData] =
        useState<SanitizedCollection<TResource> | null>();

    const cache = useRef(initialCache);

    const currentUpdate = useRef(updateList);

    const currentParams = useRef<SearchParams | null>(null);

    const [error, setError] = useState('');

    const [currentPage, setCurrentPage] = useState(params?.page ?? 1);

    const [isLoading, setIsLoading] = useState(false);

    const [isMounted, setIsMounted] = useState(true);

    const controller = new AbortController();

    const signal = controller.signal;

    const getPaginatedData = async (
        params: SearchParams,
        signal: AbortSignal
    ) => {
        const url = _server.baseUrl + source;
        /**
         * Sometimes, our configured endpoints in _server already contains query parameters.
         * Thus we need to strip these and merge with additional parameters.
         */
        const { url: strippedUrl, query } = queryString.parseUrl(url);

        try {
            const searchParams = {
                ...params,
                ...query
            };

            const response = await apiGet<CollectionResponse<TResource>>(
                strippedUrl,
                {
                    params: searchParams,
                    signal
                }
            );

            const sanitizedCollectionResponse =
                sanitizeCollectionResponse<TResource>(response);

            return sanitizedCollectionResponse;
        } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
                setError('Failed loading resources. Try again!');
            }
            throw new Error('failed');
        }
    };

    const getAndSetData = async (pageNumber: number) => {
        setIsLoading(true);

        try {
            const collectionData = await getPaginatedData(
                {
                    ...params,
                    page: pageNumber
                },
                signal
            );

            if (isMounted) {
                setPaginatedData(collectionData);
                cache.current = {
                    ...cache.current,
                    [currentPage]: collectionData
                };

                setIsLoading(false);
            }
        } catch (error) {
            setIsLoading(false);
        }
    };

    const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);

    const preFetchNextPage = async (nextPage: number) => {
        try {
            setIsLoadingNextPage(true);
            const nextCollection = await getPaginatedData(
                {
                    ...params,
                    page: nextPage
                },
                signal
            );
            if (isMounted) {
                cache.current = {
                    ...cache.current,
                    [nextPage]: nextCollection
                };
            }
        } catch (error) {
            // Silently, just fail...
            return;
        } finally {
            setIsLoadingNextPage(false);
        }
    };

    const handlePaginationClick = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const updateUrl = (nextParams: Record<string, string | number>) => {
        if (isUpdateLocationEnabled && params) {
            const otherParams = queryString.parse(location.search);
            const finalParams = {
                ...omit(
                    [
                        'channelSlug',
                        'order[updatedAt]',
                        'order[duration]',
                        'createdByCurrentUser'
                    ],
                    params
                ),
                ...nextParams,
                ...otherParams
            };
            navigate({
                pathname: location.pathname,
                search: queryString.stringify(
                    {
                        ...finalParams,
                        page: currentPage
                    },
                    { skipEmptyString: true }
                )
            });   
        }
    };

    useEffect(() => {
        setIsMounted(true);

        return () => {
            if (isLoading) {
                controller.abort();
            }
            setIsMounted(false);
        };
    }, []);

    const shouldResetCache = (
        curParams: SearchParams,
        nextParams: SearchParams
    ) => {
        const fixedParams = ['page'];
        const partialCurrentParams = omit(fixedParams, curParams);
        const partialNextParams = omit(fixedParams, nextParams);

        if (equals(partialCurrentParams, partialNextParams)) {
            return false;
        }

        return true;
    };

    const shouldUpdateList = (curUpdate, nextUpdate) => {
        if (equals(curUpdate, nextUpdate)) {
            return false;
        }
        return true;
    };

    useEffect(() => {
        /**
         * If sorting params change or list changed, we must reset the cache and go to page 1
         * So that new data can be fetched from the API. Order of pages has changed.
         */
        if (
            (
                currentParams.current &&
                params &&
                shouldResetCache(currentParams.current, params)
            )
            ||
            shouldUpdateList(currentUpdate.current, updateList)
        ) {
            cache.current = initialCache;
            //@ts-ignore
            currentParams.current = params;
            currentUpdate.current = updateList;

            setPaginatedData(null);
            setCurrentPage(1);
            getAndSetData(1);

            return;
        }

        if (params) {
            currentParams.current = params;
        }

        if (currentPage in cache.current) {
            return setPaginatedData(cache.current[currentPage]);
        } else if (!isLoading) {
            getAndSetData(currentPage);
        }
    }, [
        source,
        params,
        currentPage,
        updateList,
    ]);

    useEffect(() => {
        if (scrollIntoViewElement) {
            scrollIntoViewElement.scrollIntoView();
        }
    }, [paginatedData]);

    /**
     * Effect to handle pre-fetching of next page
     */
    useEffect(() => {
        const nextPage = Number(currentPage) + 1;

        if (
            preFetch &&
            paginatedData &&
            currentPage < paginatedData?.totalPages &&
            !isLoadingNextPage
        ) {
            preFetchNextPage(nextPage);
        }
    }, [paginatedData, currentPage]);

    useEffect(() => {
        if (params) {
            updateUrl({ page: currentPage });
        }
    }, [currentPage]);

    if (isLoading) return <Loader />;

    if (error) return <ErrorMessage message={error} />;

    return (
        <>
            {paginatedData &&
                children(
                    paginatedData.list,
                    paginatedData.totalItems,
                    isLoading
                )
            }

            {paginatedData && paginatedData.totalPages > 1 && (
                <Pagination
                    totalPages={paginatedData.totalPages}
                    currentPage={currentPage}
                    onClick={handlePaginationClick}
                />
            )}
        </>
    );
};

export default Paginator;